import { DateTime } from 'luxon';
import { OrderType } from '../order/order';

export type ListProductPriceRequest = {
  readonly productIds: number[];
  readonly locationId?: number;
  readonly orderId?: number;
  readonly orderProductId?: number;
  readonly userId?: number;
  readonly type?: OrderType | 'All';
};

export type ProductPrice = RentalProductPrice | SaleProductPrice;

export type RentalProductPrice = {
  readonly id: string;
  readonly productId: number;
  readonly type: OrderType.Rental;
  readonly price: number;
  readonly from?: DateTime | undefined;
  readonly to?: DateTime | undefined;
  readonly durationValue: number;
  readonly durationUnit: DurationUnit;
  readonly durationMinutes: number;
  readonly durationLabel?: string | undefined;

  readonly recurringType?: RecurringType | undefined;
  readonly recurringDaysOfWeek?: number[] | undefined; // 1-7 where 1 is Monday and 7 is Sunday
  readonly recurringWeeksOfMonth?: number[] | undefined; // -1 or 1-5 where -1 means last week of month
  readonly recurringDaysOfMonth?: number[] | undefined; // -1 or 1-31 where -1 means last day of month

  readonly name?: string | undefined;
  readonly featured: boolean;
  readonly createdAt: DateTime;
  readonly deletedAt?: DateTime | undefined;
  readonly locationIds: number[] | undefined;
  readonly orderId?: number;
  readonly orderProductId?: number;
  readonly originalPrice?: RentalProductPrice;
  readonly userId?: number;
  readonly tax?: number; // Tax amount for this price, if applicable
  readonly taxPercentage?: number; // Tax percentage for this price, if applicable
};

export enum RecurringType {
  Weekly = 'Weekly',
  Biweekly = 'Biweekly',
  DateMonthly = 'DateMonthly', // ex: 1st and 10th of each month
  DayOfWeekMonthly = 'DayOfWeekMonthly', // ex: first Monday of each month, third Friday of each month
}

export type SaleProductPrice = {
  readonly id: string;
  readonly productId: number;
  readonly userId?: number;
  readonly type: OrderType.Sale;
  readonly price: number;
  readonly from: DateTime | undefined;
  readonly to: DateTime | undefined;
  readonly name: string | undefined;
  readonly featured: boolean;
  readonly createdAt: DateTime;
  readonly deletedAt: DateTime | undefined;
  readonly locationIds: number[] | undefined;
  readonly tax?: number; // Tax amount for this price, if applicable
  readonly taxPercentage?: number; // Tax percentage for this price, if applicable
};

export enum DurationUnit {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}

export type CustomProductPricingConfig = {
  id: string;
  type: OrderType;
  durationValue: number;
  durationUnit: DurationUnit;
  createdAt: DateTime;
  updatedAt: DateTime;
};

export type CustomProductPricingConfigRequest = Omit<CustomProductPricingConfig, 'id' | 'createdAt' | 'updatedAt'>;

export type CreateProductPriceRequest = CreateRentalProductPriceRequest | CreateSaleProductPriceRequest;
export type CreateRentalProductPriceRequest = Omit<
  RentalProductPrice,
  'id' | 'durationMinutes' | 'createdAt' | 'deletedAt' | 'createdBy' | 'deletedBy'
>;
export type CreateSaleProductPriceRequest = Omit<
  SaleProductPrice,
  'id' | 'durationMinutes' | 'createdAt' | 'deletedAt'
>;
export type EditProductPriceRequest = EditRentalProductPriceRequest | EditSaleProductPriceRequest;
export type EditRentalProductPriceRequest = Omit<
  RentalProductPrice,
  'id' | 'productId' | 'durationMinutes' | 'createdAt' | 'deletedAt'
>;
export type EditSaleProductPriceRequest = Omit<
  SaleProductPrice,
  'id' | 'productId' | 'durationMinutes' | 'createdAt' | 'deletedAt'
>;

export type CustomOrderPriceRequest = {
  readonly id?: string; // product price id if price exists
  readonly productId: number;
  readonly orderProductId?: number;
  readonly orderId?: number;
  readonly type: OrderType.Rental;
  readonly durationValue: number;
  readonly durationUnit: DurationUnit;
  readonly price: number;
  readonly action?: 'add' | 'remove' | 'update';
  readonly idToChange?: string;
};

export type CustomUserPriceRequest = {
  readonly id?: string; // product price id if price exists
  readonly productId: number;
  readonly userId?: number;
  readonly type: OrderType.Rental;
  readonly durationValue: number;
  readonly durationUnit: DurationUnit;
  readonly price: number;
  readonly from?: DateTime | undefined;
  readonly to?: DateTime | undefined;
  readonly action?: 'add' | 'remove' | 'update';
  readonly idToChange?: string;
};

export type CalculateRentalPriceRequest = {
  readonly productId: number;
  readonly type: OrderType.Rental;
  readonly start: DateTime;
  readonly end: DateTime;
  readonly locationId?: number;
  readonly orderId?: number;
  readonly orderProductId?: number;
  readonly userId?: number;
  readonly customOrderRentalRates?: CustomOrderPriceRequest[];
  readonly taxInclusive?: boolean;
  readonly taxCodeId?: string;
};

export type CalculateSalePriceRequest = {
  readonly productId: number;
  readonly type: OrderType.Sale;
  readonly locationId?: number;
  readonly userId?: number;
  readonly taxInclusive?: boolean;
  readonly taxCodeId?: string;
};

export type CalculatePriceRequest = CalculateRentalPriceRequest | CalculateSalePriceRequest;

export type RentalComponentPrice = {
  readonly unitPrice: RentalProductPrice;
  readonly consumedUnits: number;
  readonly durationMinutes: number;
  readonly price: number;
  readonly taxedPrice?: number;
};

export type CalculateRentalPriceResult = CalculateRentalPriceRequest & {
  readonly totalPrice: number;
  readonly totalDurationMinutes: number;
  readonly componentPrices: RentalComponentPrice[];
  readonly taxedPrice?: number;
  readonly taxPercentage?: number;
};

export type CalculateSalePriceResult = CalculateSalePriceRequest & {
  readonly totalPrice: number;
  readonly unitPrice: SaleProductPrice;
  readonly taxedPrice?: number;
  readonly taxPercentage?: number;
};

export type ErrorCalculatePriceResult = CalculatePriceRequest & { readonly error: string };

export type CalculatePriceResult = CalculateRentalPriceResult | CalculateSalePriceResult | ErrorCalculatePriceResult;

export const isErrorCalculatePriceResult = (r: CalculatePriceResult): r is ErrorCalculatePriceResult => {
  return Object.hasOwn(r, 'error');
};

export type IntervalString = { start: string; end: string };
export type DeadDaysConfigHours = {
  // undefined means free
  mon: IntervalString | undefined;
  tue: IntervalString | undefined;
  wed: IntervalString | undefined;
  thu: IntervalString | undefined;
  fri: IntervalString | undefined;
  sat: IntervalString | undefined;
  sun: IntervalString | undefined;
};

export type DeadDaysConfig = {
  categoryId?: number;
  hours: DeadDaysConfigHours;
};
